// import node module libraries
import React, { useContext, Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  useAccordionButton,
  AccordionContext,
  ListGroup,
  Badge
} from 'react-bootstrap';
import { checkQuizAvailability } from 'helper/utils';

const GKAccordionModule = ({ accordionItems, itemClass, onSelected, currTopic }) => {

  const ContextAwareToggle = ({ children, eventKey, callback }) => {
    const { activeEventKey } = useContext(AccordionContext);

    const decoratedOnClick = useAccordionButton(
      eventKey,
      () => callback && callback(eventKey)
    );

    const isCurrentEventKey = activeEventKey === eventKey;

    return (
      <Fragment>
        <div
          onClick={decoratedOnClick}
          className={`section-toggle ${isCurrentEventKey ? 'active' : ''}`}
        >
          <div className="section-header-content">
            <div className="section-icon">
              <i className={`fe fe-${children?.icon}`}></i>
            </div>
            <div className="section-info">
              <h6 className="section-title">{children?.title}</h6>
              <span className="section-count">
                {children?.items?.length || 0} {children?.items?.length === 1 ? 'item' : 'items'}
              </span>
            </div>
          </div>
          <div className="section-chevron">
            <i className={`fe fe-chevron-${isCurrentEventKey ? 'up' : 'down'}`}></i>
          </div>
        </div>

        <style jsx>{`
          .section-toggle {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem 1.25rem;
            cursor: pointer;
            transition: all 0.3s ease;
            border-radius: 8px;
            margin-bottom: 0.5rem;
          }

          .section-toggle:hover {
            background: #f8f9fa;
          }

          .section-toggle.active {
            background: linear-gradient(135deg, rgba(246, 133, 33, 0.05) 0%, rgba(29, 158, 176, 0.05) 100%);
          }

          .section-header-content {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex: 1;
          }

          .section-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 1.125rem;
            flex-shrink: 0;
            box-shadow: 0 4px 8px rgba(246, 133, 33, 0.2);
          }

          .section-info {
            flex: 1;
          }

          .section-title {
            font-size: 0.9375rem;
            font-weight: 700;
            color: #1e293b;
            margin: 0 0 0.25rem 0;
          }

          .section-count {
            font-size: 0.75rem;
            color: #94a3b8;
            font-weight: 500;
          }

          .section-chevron {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #64748b;
            transition: all 0.3s ease;
          }

          .section-toggle:hover .section-chevron {
            background: #e2e8f0;
          }

          .section-toggle.active .section-chevron {
            background: #F68521;
            color: white;
          }
        `}</style>
      </Fragment>
    );
  };

  const getContentIcon = (contentType, icon, isLocked) => {
    if (isLocked) return 'lock';
    
    const iconMap = {
      'video': 'video',
      'file': 'file-text',
      'image': 'image',
      'links': 'link',
      'default': icon || 'book-open'
    };

    return iconMap[contentType] || iconMap.default;
  };

  const getContentColor = (contentType) => {
    const colorMap = {
      'video': '#F68521',
      'file': '#1d9eb0',
      'image': '#70a553',
      'links': '#7c3aed',
      'default': '#64748b'
    };

    return colorMap[contentType] || colorMap.default;
  };

  return (
    <Fragment>
      <Accordion defaultActiveKey={accordionItems[0]?.id}>
        <ListGroup as="ul" variant="flush" className="course-content-list">
          {accordionItems?.map((item, index) => {
            const topics = item.items;
            
            if (!topics || topics.length === 0) {
              return (
                <ListGroup.Item
                  key={index}
                  as="li"
                  className="content-section-item"
                >
                  <ContextAwareToggle eventKey={item?.id}>
                    {item}
                  </ContextAwareToggle>
                  <Accordion.Collapse eventKey={item?.id}>
                    <div className="empty-section">
                      <i className="fe fe-inbox empty-icon"></i>
                      <p className="empty-text">No content available</p>
                    </div>
                  </Accordion.Collapse>
                </ListGroup.Item>
              );
            } else {
              return (
                <ListGroup.Item
                  key={index}
                  as="li"
                  className="content-section-item"
                >
                  <ContextAwareToggle eventKey={item?.id}>
                    {item}
                  </ContextAwareToggle>
                  <Accordion.Collapse eventKey={item?.id}>
                    <ListGroup className="topic-list" as="ul">
                      {topics?.map((subitem, subindex) => {
                        const disableItem = (item?.title === "Quizzes") ? !checkQuizAvailability(subitem) : false;
                        const isLink = subitem?.contentType === 'links';
                        const isActive = currTopic?.guid === subitem.guid;
                        const contentIcon = getContentIcon(subitem?.contentType, item?.icon, disableItem);
                        const contentColor = getContentColor(subitem?.contentType);

                        return (
                          <ListGroup.Item
                            key={subindex}
                            as="li"
                            className={`topic-item ${isActive ? 'active' : ''} ${disableItem ? 'disabled' : ''}`}
                            onClick={() => !disableItem && onSelected(subitem)}
                          >
                            <Link
                              to={isLink ? subitem?.name : '#'}
                              target={isLink ? '_blank' : ''}
                              className="topic-link"
                              onClick={(e) => !isLink && e.preventDefault()}
                            >
                              <div className="topic-info">
                                <div 
                                  className="topic-icon-wrapper"
                                  style={{ 
                                    background: isActive ? contentColor : 'transparent',
                                    border: `2px solid ${isActive ? contentColor : '#e2e8f0'}`
                                  }}
                                >
                                  <i 
                                    className={`fe fe-${contentIcon} topic-icon`}
                                    style={{ color: isActive ? 'white' : contentColor }}
                                  ></i>
                                </div>
                                <div className="topic-content">
                                  <span className="topic-title">
                                    {subitem?.title || subitem?.name || subitem?.contentType}
                                  </span>
                                  {disableItem && (
                                    <Badge bg="" className="locked-badge">
                                      <i className="fe fe-lock me-1"></i>
                                      Locked
                                    </Badge>
                                  )}
                                  {isLink && (
                                    <i className="fe fe-external-link external-icon"></i>
                                  )}
                                </div>
                              </div>
                              {subitem?.duration && (
                                <div className="topic-duration">
                                  <i className="fe fe-clock me-1"></i>
                                  {subitem.duration}
                                </div>
                              )}
                            </Link>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </Accordion.Collapse>
                </ListGroup.Item>
              );
            }
          })}
        </ListGroup>
      </Accordion>

      <style jsx>{`
        /* List Styling */
        .course-content-list {
          border: none;
        }

        .content-section-item {
          border: none;
          padding: 0;
          margin-bottom: 0.5rem;
        }

        /* Empty Section */
        .empty-section {
          text-align: center;
          padding: 2rem 1rem;
          background: #f8f9fa;
          border-radius: 8px;
          margin: 0.5rem 1.25rem 1rem;
        }

        .empty-icon {
          font-size: 2rem;
          color: #cbd5e1;
          margin-bottom: 0.5rem;
        }

        .empty-text {
          color: #94a3b8;
          font-size: 0.875rem;
          margin: 0;
        }

        /* Topic List */
        .topic-list {
          border: none;
          padding: 0.5rem 0.5rem 1rem;
        }

        .topic-item {
          border: none;
          padding: 0;
          margin-bottom: 0.5rem;
          background: transparent;
          transition: all 0.3s ease;
        }

        .topic-item.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .topic-item:not(.disabled):hover {
          transform: translateX(4px);
        }

        .topic-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.875rem 1rem;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          text-decoration: none;
          color: inherit;
          transition: all 0.3s ease;
          gap: 1rem;
        }

        .topic-item:not(.disabled):hover .topic-link {
          border-color: #F68521;
          background: rgba(246, 133, 33, 0.02);
          box-shadow: 0 4px 8px rgba(246, 133, 33, 0.1);
        }

        .topic-item.active .topic-link {
          background: linear-gradient(135deg, rgba(246, 133, 33, 0.05) 0%, rgba(29, 158, 176, 0.05) 100%);
          border-color: #F68521;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.15);
        }

        .topic-item.disabled .topic-link {
          cursor: not-allowed;
        }

        /* Topic Info */
        .topic-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }

        .topic-icon-wrapper {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .topic-icon {
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .topic-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
          min-width: 0;
        }

        .topic-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .locked-badge {
          background: rgba(220, 38, 38, 0.1) !important;
          color: #dc2626 !important;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-flex;
          align-items: center;
        }

        .external-icon {
          font-size: 0.75rem;
          color: #94a3b8;
        }

        /* Topic Duration */
        .topic-duration {
          display: flex;
          align-items: center;
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 500;
          white-space: nowrap;
        }

        .topic-duration i {
          font-size: 0.875rem;
          color: #94a3b8;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .section-toggle {
            padding: 0.875rem 1rem;
          }

          .section-icon {
            width: 36px;
            height: 36px;
            font-size: 1rem;
          }

          .section-title {
            font-size: 0.875rem;
          }

          .topic-link {
            padding: 0.75rem 0.875rem;
          }

          .topic-title {
            font-size: 0.8125rem;
          }

          .topic-duration {
            font-size: 0.6875rem;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default GKAccordionModule;